import { Component, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { lastValueFrom } from 'rxjs';
import { AuthService } from 'src/app/services/auth.service';
import { LanguageService } from 'src/app/services/language.service';
import { QuestionnaireService } from 'src/app/services/questionnaire.service';
import { Baseline, Chapter, Questionnaire } from 'src/app/shared/models/questionnaire.model';

@Component({
    selector: 'app-create-project-questionnaire',
    templateUrl: './create-project-questionnaire.component.html',
    styleUrl: './create-project-questionnaire.component.scss',
    standalone: false
})
export class CreateProjectQuestionnaireComponent implements OnInit {
	@ViewChild('viewQuestionModal') viewQuestionModal!: TemplateRef<any>;
	@ViewChild('successModal') successModal!: TemplateRef<any>;

	steps = ['Questionnaire details', 'Chapter & Questions', 'Summary'];
	today = Date.now();
	step = 1;
  currentStep = 0;
	submitted = false;
	spinner = false;
	editMode = false;
	loading = true;
	questionnaireBaslineLoading = true;

	success = false;
	successMessage = '';
	message = '';
	
	project_id: any;
	questionnaire_id: any;
	listLang: any[] = [];
	themeList: any[] = [];

	questionnaire_name!: string;
	description!: string;
	selectedLanguage = 'en';
	selectedBaseline: Baseline | null = null;
	status: any = null;

	listStatus: any[] = [
		{ name: 'DRAFT', status_color: 'info'},
		{ name: 'ACTIVE', status_color: 'success'},
		{ name: 'PAUSED', status_color: 'warning'},
		{ name: 'CANCELED', status_color: 'danger'}
	];

	chapters: Chapter[] = [];
	baselines: Baseline[] = [];
	chaptersError = false;
	questionLength = 0;

	removeChapters: any[] = [];
	removeQuestions: any[] = [];

	constructor(private router: Router, private route: ActivatedRoute, private modalService: NgbModal, private questionnaireService: QuestionnaireService, private authService: AuthService, private languageService: LanguageService) {}

	async ngOnInit() {
		this.loading = true;
		this.project_id = this.route.snapshot.paramMap.get('projectid');
		this.questionnaire_id = this.route.snapshot.paramMap.get('questionnaireid');
		this.listLang = this.languageService.listLang();

		if (this.questionnaire_id) {
			this.questionnaireService.getProjectQuestionnaire(this.project_id, this.questionnaire_id).subscribe({
				next: (data: Questionnaire) => {
					this.questionnaire_name = data.name;
					this.status = this.listStatus.find(item => item.name === data.status);
					this.selectedLanguage = data.lang_code;
					this.selectedBaseline = data.baseline;
					this.description = data.description
					this.editMode = true;

					this.chapters = data.chapters;
					this.questionnaireBaslineLoading = false;
				},
				error: (err) => {
					this.authService.toastErrorMessage(err.message ? err.message : err, 'Getting Questionnaire');
				}
			});
		} else {
			await this.getBaselines();
		}

		await this.getGeneralList();

		this.loading = false;
	}

	async getBaselines() {
		this.questionnaireBaslineLoading = true;
    try {
      const data = await lastValueFrom(this.questionnaireService.getQuestionnaireBaselines(this.project_id, this.selectedLanguage)); // Assuming 'en' for language
      this.baselines = data.questionnaire_baselines;
      if (this.baselines.length === 1) {
        this.selectedBaseline = this.baselines[0];
      }
			this.questionnaireBaslineLoading = false;
    } catch (err: any) {
			this.authService.toastErrorMessage(err.message ? err.message : err, 'Getting Theme list');
			this.questionnaireBaslineLoading = false;
    }
  }

  async getGeneralList() {
    try {
      const list = await lastValueFrom(this.questionnaireService.getQuestionnaireGeneralList(this.project_id, 'questionnaire_theme'));
      this.themeList = list.values;
    } catch (err: any) {
      this.authService.toastErrorMessage(err.message ? err.message : err, 'Getting Theme list');
    }
  }

	async onLanguageChange() {
		this.selectedBaseline = null;
    await this.getBaselines();
  }

	backStep() {
    if (this.currentStep > 0) {
      this.currentStep--;
    }
  }

	nextStep() {
		this.submitted = true;

		switch (this.currentStep) {
			case 0:
				if (this.checkQuestionnaireDetails()) {
					this.currentStep++;
					this.submitted = false;
				}
				break;
			case 1:
				if (this.checkChaptersQuestionsValidity() || this.status.name !== 'ACTIVE') {
					this.currentStep++;
					this.questionLength = 0;
					this.chapters.forEach(chapter => {
						this.questionLength += chapter.questions.length;
					});
					this.submitted = false;
				}
				break;
		}
  }

	checkQuestionnaireDetails(): boolean {
		if (!this.questionnaire_name || this.questionnaire_name === '' ) {
			return false;
		}

		if (!this.selectedLanguage){
			return false;
		}

		if (!this.status){
			return false;
		}

		if (!this.selectedBaseline){
			return false;
		}

		return true;
	}

	checkChaptersQuestionsValidity(): boolean {
		let valid = true;
		this.chaptersError = false;

		if (this.chapters.length === 0) {
			valid = false;
			this.chaptersError = true;
		}

		this.chapters.forEach(chapter => {
			if (chapter.status) {
				delete chapter.status;
			}

			if (chapter.questions.length === 0) {
				valid = false;
				chapter.status = 'error';
			}

			chapter.questions.forEach(question => {
				if (question.status) {
					delete question.status;
				}
	
				if (!question.response.response_type) {
					valid = false;
					question.status = 'error';
					chapter.status = 'error';
				}
	
				if ((question.body.question === '' || !question.body.question || question.body.question.length < 20) && (!question.body.link_url || !question.body.link_type)) {
					valid = false;
					question.status = 'error';
					chapter.status = 'error';
				}
	
				if (question.time_limit < 0 || question.count_down < 0) {
					valid = false;
					question.status = 'error';
					chapter.status = 'error';
				}
	
				switch (question.response.response_type) {
					case 'SELECT':
						if (question.response.response_select) {
							if (question.response.response_select.response_options.length < 2) {
								valid = false;
								question.status = 'error';
								chapter.status = 'error';
							}
						}
						break;
					case 'RANGE':
						if (question.response.response_range) {
							if (question.response.response_range.range_min >= question.response.response_range.range_max) {
								valid = false;
								question.status = 'error';
								chapter.status = 'error';
							}
		
							if (!question.response.response_range.range_label_max || !question.response.response_range.range_label_min) {
								valid = false;
								question.status = 'error';
								chapter.status = 'error';
							}
						}
						break;
				}
			});
		});

		return valid;
	}

	onChaptersRemoved(removedChapters: any[]) {
    this.removeChapters = removedChapters;
  }

	onQuestionRemoved(removedQuestions: any[]) {
    this.removeQuestions = removedQuestions;
  }

	async saveQuestionnaire() {
		this.submitted = true;
		this.spinner = true;

		await this.removeResponseChanges();

		const data = {
			questionnaire: {
				name: this.questionnaire_name,
				status: this.status.name,
				lang_code: this.selectedLanguage,
				description: this.description,
				baseline_id: this.selectedBaseline?.id
			},
			chapters: this.chapters,
			removeChapters: this.removeChapters,
			removeQuestions: this.removeQuestions
		}

		if (this.editMode) {
			this.questionnaireService.updateProjectQuestionnaire(this.project_id, this.questionnaire_id, data).subscribe({
				next: () => {
					this.submitted = false;
					this.spinner = false;
					this.success = true;
					this.successMessage = 'Questionnaire saved successfully.';
	
					this.modalService.open(this.successModal, { size: 'l', backdrop: 'static', keyboard : false });
				},
				error: (err) => {
					this.submitted = false;
					this.spinner = false;
					this.success = false;
					this.successMessage = 'Questionnaire can not be saved!';
					this.message = err.message ? err.message : err;
	
					this.modalService.open(this.successModal, { size: 'l', backdrop: 'static', keyboard : false });
				}
			});
		} else {	
			this.questionnaireService.createProjectQuestionnaire(this.project_id, data).subscribe({
				next: () => {
					this.submitted = false;
					this.spinner = false;
					this.success = true;
					this.successMessage = 'Questionnaire saved successfully.';
	
					this.modalService.open(this.successModal, { size: 'l', backdrop: 'static', keyboard : false });
				},
				error: (err) => {
					this.submitted = false;
					this.spinner = false;
					this.success = false;
					this.successMessage = 'Questionnaire can not be saved!';
					this.message = err.message ? err.message : err;
	
					this.modalService.open(this.successModal, { size: 'l', backdrop: 'static', keyboard : false });
				}
			});
		}
	}

	async removeResponseChanges() {
		for await (const chapter of this.chapters) {
			for await (const question of chapter.questions) {
				switch (question.response.response_type) {
					case 'SELECT':
						if (question.response.response_range) {
							delete question.response.response_range;
						}

						if (question.response.response_voice) {
							delete question.response.response_voice;
						}
						break;
					case 'RANGE':
						if (question.response.response_select) {
							delete question.response.response_select;
						}

						if (question.response.response_voice) {
							delete question.response.response_voice;
						}
						break;
					case 'VOICE':
						if (question.response.response_select) {
							delete question.response.response_select;
						}

						if (question.response.response_range) {
							delete question.response.response_range;
						}
						break;
					case 'VISUAL':
						if (question.response.response_select) {
							delete question.response.response_select;
						}

						if (question.response.response_range) {
							delete question.response.response_range;
						}
						if (question.response.response_voice) {
							delete question.response.response_voice;
						}
						break;
				}
			}
		}
	}

	closeModal() {
		if (this.success) {
			this.editMode = false;
			this.questionnaireService.refreshList();
			this.router.navigate(['/projects', this.project_id], { queryParams: { tab: 'questionnaires' } });
			this.modalService.dismissAll();
		} else {
			this.modalService.dismissAll();
		}
	}

}
